import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft, BookOpen, Newspaper, Clock, Tag,
  Globe2, Shield, TrendingUp, Leaf, Swords, Star,
  Landmark, Users, Cpu, Search, X, Loader2, AlertCircle,
  Flame, Anchor, Plane, CheckCircle2, StickyNote, RotateCcw,
} from "lucide-react";
import articlesApi from "../api/articles";
import studyApi from "../api/studyProgress";
import SEOHead from "../components/SEOHead";
import { useAuth } from "../hooks/useAuth";

const CAT_CFG = {
  "All":                  { color: "#1A2810", bg: "#F0FDF4", dot: "#16A34A",  Icon: Newspaper },
  "Economy":              { color: "#064E3B", bg: "#ECFDF5", dot: "#10B981",  Icon: TrendingUp },
  "National":             { color: "#92400E", bg: "#FEF3C7", dot: "#D97706",  Icon: Landmark },
  "International":        { color: "#1E3A8A", bg: "#EFF6FF", dot: "#3B82F6",  Icon: Globe2 },
  "Defence":              { color: "#7C2D12", bg: "#FFF7ED", dot: "#EA580C",  Icon: Shield },
  "Government Schemes":   { color: "#1E40AF", bg: "#EFF6FF", dot: "#2563EB",  Icon: BookOpen },
  "Science & Technology": { color: "#312E81", bg: "#EEF2FF", dot: "#6366F1",  Icon: Cpu },
  "Environment":          { color: "#14532D", bg: "#F0FDF4", dot: "#22C55E",  Icon: Leaf },
  "Society":              { color: "#4C1D95", bg: "#F5F3FF", dot: "#7C3AED",  Icon: Users },
  "Analysis":             { color: "#831843", bg: "#FDF2F8", dot: "#EC4899",  Icon: Star },
  "Miscellaneous":        { color: "#374151", bg: "#F3F4F6", dot: "#9CA3AF",  Icon: Tag },
};

const BRANCH_CFG = {
  army:     { label: "Army",      Icon: Swords, color: "#4D7C0F", bg: "#F7FEE7", ring: "#84CC16" },
  navy:     { label: "Navy",      Icon: Anchor, color: "#1E3A8A", bg: "#EFF6FF", ring: "#3B82F6" },
  airforce: { label: "Air Force", Icon: Plane,  color: "#075985", bg: "#F0F9FF", ring: "#0EA5E9" },
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${parseInt(d, 10)} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}
function catCfg(key) { return CAT_CFG[key] || CAT_CFG["Miscellaneous"]; }

/* ── Article card ─────────────────────────────────────────────────────────── */
function ArticleCard({ item, onClick, progress }) {
  const cfg = catCfg(item.category);
  const { Icon } = cfg;
  const isCompleted = progress?.completed;
  const hasNote     = !!progress?.note?.trim();
  const isRevisit   = progress?.revisit;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md cursor-pointer overflow-hidden group transition-shadow relative"
    >
      <div className="h-1 w-full" style={{ background: isCompleted ? '#34D399' : cfg.dot }} />
      <div className="p-4">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: cfg.bg }}>
              <Icon size={11} style={{ color: cfg.color }} />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: cfg.color }}>
              {item.category}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {isRevisit && (
              <span title="Marked for revisit" className="w-5 h-5 rounded-full bg-yellow-50 flex items-center justify-center">
                <RotateCcw size={11} className="text-yellow-500" />
              </span>
            )}
            {hasNote && (
              <span title="Has notes" className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center">
                <StickyNote size={11} className="text-blue-400" />
              </span>
            )}
            {isCompleted && (
              <span title="Completed" className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 size={11} className="text-emerald-500" />
              </span>
            )}
            {!isCompleted && item.featured && (
              <span className="flex items-center gap-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600">
                <Flame size={9} /> Featured
              </span>
            )}
          </div>
        </div>

        <h3 className="font-sora font-bold text-sm text-gray-900 leading-snug mb-2 group-hover:text-indigo-700 transition-colors line-clamp-2">
          {item.title}
        </h3>

        {item.summary && (
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">{item.summary}</p>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-gray-50">
          <span className="text-[10px] text-gray-400 font-medium">{fmtDate(item.dateStr)}</span>
          {item.readTime && (
            <span className="flex items-center gap-1 text-[10px] text-gray-400">
              <Clock size={9} /> {item.readTime} min read
            </span>
          )}
          {isCompleted && (
            <span className="ml-auto text-[11px] font-bold text-emerald-500 flex items-center gap-1">
              <CheckCircle2 size={12} /> Done
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Header ──────────────────────────────────────────────────────────────── */
function ArticlesHeader() {
  const navigate = useNavigate();
  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
        <button onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0">
          <ChevronLeft size={15} /> Home
        </button>
        <span className="text-gray-200">|</span>
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white shadow-sm">
            <BookOpen size={12} /> Articles
          </button>
          <button onClick={() => navigate("/ca")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-gray-500 hover:bg-green-50 transition-all">
            <Newspaper size={12} /> Current Affairs
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => navigate("/my-notes")}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 transition-all">
            <StickyNote size={12} /> My Notes
          </button>
          <span className="text-[10px] text-gray-400 font-medium hidden sm:block">
            NDA · CDS · AFCAT
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
const CATEGORIES = Object.keys(CAT_CFG);

export default function ArticlesPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();

  const [items, setItems]              = useState([]);
  const [loading, setLoading]          = useState(true);
  const [error, setError]              = useState(null);
  const [activeCategory, setActiveCat] = useState("All");
  const [searchQ, setSearchQ]          = useState("");
  const [progressMap, setProgressMap]  = useState(new Map()); // slug → progress

  // Branch filter comes from URL param set by the global BranchSidebar
  const urlParams    = new URLSearchParams(location.search);
  const activeBranch = urlParams.get("branch");
  const branchCfg    = activeBranch ? BRANCH_CFG[activeBranch] : null;

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = {};
    if (activeCategory !== "All") params.category = activeCategory;
    articlesApi.list(params)
      .then(d => setItems(d.items || []))
      .catch(() => setError("Failed to load articles."))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  useEffect(() => {
    if (!user) return;
    studyApi.all()
      .then(all => {
        const map = new Map();
        all.filter(r => r.itemType === 'article').forEach(r => map.set(r.itemSlug, r));
        setProgressMap(map);
      })
      .catch(() => {});
  }, [user]);

  const filtered = items.filter(a => {
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      if (!a.title.toLowerCase().includes(q) && !(a.summary || "").toLowerCase().includes(q)) return false;
    }
    if (activeBranch && !a.tags?.includes(activeBranch)) return false;
    return true;
  });

  function clearBranch() {
    const p = new URLSearchParams(location.search);
    p.delete("branch");
    navigate(`/articles${p.toString() ? "?" + p.toString() : ""}`, { replace: true });
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      <SEOHead
        title="Defence Exam Articles — NDA, CDS, CAPF Study Material | EduPulseAI"
        description="In-depth articles for NDA, CDS, AFCAT and CAPF preparation. Covers current affairs analysis, history, geography, science & technology, polity and economy. Free study material."
        keywords="NDA articles, CDS study material, CAPF preparation articles, defence exam notes, UPSC articles"
        url="/articles"
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", "name": "Defence Exam Articles", "url": "https://edupulseai.co.in/articles", "publisher": { "@type": "Organization", "name": "EduPulseAI" } }}
      />
      <ArticlesHeader />

      {/* ── Controls ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[200px] max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-8 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-indigo-500 transition-colors"
            />
            {searchQ && (
              <button onClick={() => setSearchQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                <X size={13} className="text-gray-400" />
              </button>
            )}
          </div>
          {!loading && (
            <div className="ml-auto text-right">
              <p className="font-sora font-black text-lg text-gray-900 leading-none">{filtered.length}</p>
              <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">articles</p>
            </div>
          )}
        </div>

        {/* Category filter — hidden when a branch is active */}
        {!activeBranch && <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
            {CATEGORIES.map(key => {
              const c = CAT_CFG[key];
              return (
                <button
                  key={key}
                  onClick={() => { setActiveCat(key); setSearchQ(""); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex-shrink-0 border"
                  style={activeCategory === key
                    ? { background: c.color, color: "#fff", borderColor: c.color }
                    : { background: "#fff", color: "#374151", borderColor: "#E5E7EB" }
                  }
                >
                  <c.Icon size={11} />
                  {key}
                </button>
              );
            })}
          </div>
        </div>}
      </div>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">

        {/* Active branch banner */}
        {branchCfg && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-xl border"
            style={{ background: branchCfg.bg, borderColor: branchCfg.ring + "66" }}
          >
            <branchCfg.Icon size={15} color={branchCfg.color} />
            <span className="text-sm font-bold" style={{ color: branchCfg.color }}>
              {branchCfg.label} News
            </span>
            <button
              onClick={clearBranch}
              className="ml-auto text-xs font-semibold flex items-center gap-1"
              style={{ color: branchCfg.color }}
            >
              <X size={12} /> Clear
            </button>
          </motion.div>
        )}

        {loading && (
          <div className="flex justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={28} className="animate-spin text-gray-300" />
              <p className="text-xs text-gray-400">Loading articles…</p>
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center py-16 gap-3">
            <AlertCircle size={32} className="text-red-400" />
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center py-20 gap-3">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
              <BookOpen size={28} className="text-gray-300" />
            </div>
            <p className="text-sm text-gray-400 font-medium">No articles found</p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((item, i) => (
              <motion.div key={item._id || item.slug}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: i * 0.03 }}
              >
                <ArticleCard
                  item={item}
                  onClick={() => navigate(`/articles/${item.slug}`)}
                  progress={progressMap.get(item.slug)}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
